import React, { useEffect, useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { employee, initEmployee } from '../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_employee } from '../../api/employee';
import CommonTableList from '../common/common-table-list';
import CommonDetail from '../common/common-detail';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import EmployeeDetail from './employee-detail';
import { isEmpty } from '../../utils/util';
import { printProfileCard } from '../../utils/record-print-utils';
import { deleteFailureMessage, writeFailureMessage } from '../../utils/write-result';
import { persistablePrivilege } from '../../auth/privilege';

const Employee: React.FC = (prop) => {

    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState([]);
    const [employee, setEmployee] = useState<employee>(initEmployee);

    const filterField: any = ['code', 'name', 'tel'];

    const placeholder_txt: string = 'รหัสพนักงาน / ชื่อพนักงาน / เบอร์โทรศัพท์' 

    const onPrintEmployee = (record: any) => {
        printProfileCard('ข้อมูลพนักงาน (EMPLOYEE PROFILE)', [
            { label: 'รหัสพนักงาน', value: record.code },
            { label: 'ชื่อพนักงาน', value: record.name },
            { label: 'เบอร์โทรศัพท์', value: record.tel },
            { label: 'ตำแหน่ง / สิทธิ์', value: record.role || 'พนักงานประจำร้าน' },
            { label: 'วันที่เริ่มงาน', value: record.create_date || '-' }
        ]);
    };

    const columns: ColumnsType<employee> = [
        { title: 'รหัสพนักงาน', dataIndex: 'code', key: 'code', fixed: 'left' },
        { title: 'ชื่อพนักงาน', dataIndex: 'name', key: 'name' },
        { title: 'เบอร์โทรศัพท์', dataIndex: 'tel', key: 'tel' },
        { title: 'การดำเนินการ', dataIndex: 'operation', width: '180px', fixed: 'right', render: (_, record) =>
            data.length >= 1 ? ( 
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record) }} />
                    <FontAwesomeIcon icon="print" title="พิมพ์ข้อมูลพนักงาน" onClick={() => { onPrintEmployee(record) }} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                    <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id) }} />
                </div>
            ) : null
        }
    ]

    const init = async () => {
        setData([]);
        let getEmployee = await api_employee('LIST', {});
        setData(getEmployee);
    }

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const onShowDetail = (mode: string, employeeData?: employee) => {
        if (mode === 'ADD' || mode === 'COPY') {
            setTitle('เพิ่มพนักงาน')
        } else if (mode === 'EDIT') {
            setTitle('แก้ไขพนักงาน')    
        } else if (mode === 'VIEW') {
            setTitle('รายละเอียดพนักงาน')
        }

        setMode(mode);
        setEmployee(employeeData !== undefined && employeeData !== null ? employeeData : initEmployee);
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(employee));
        if (employee.id > 0) {
            operation = 'UPDATE';
            key_id = employee.id;
        }
        let isPasswordChange = 'isPasswordChange' in body ? body['isPasswordChange'] : false;

        // A password is required to CREATE an employee, but not to edit one:
        // an empty box on an existing employee means "keep the current
        // password", so demanding one here would force the operator to
        // retype (or worse, resend) a credential on every unrelated edit.
        const passwordSatisfied = operation === 'INSERT' ? !isEmpty(body['password']) : true;

        if (!isEmpty(body['name']) && !isEmpty(body['tel']) && passwordSatisfied) {
            body['code'] = isEmpty(body['code']) ? null : body['code']
            body['name'] = isEmpty(body['name']) ? null : body['name']
            body['tel'] = isEmpty(body['tel']) ? null : body['tel']
            body['password'] = isEmpty(body['password']) || !isPasswordChange ? null : body['password']
            body['address'] = isEmpty(body['address']) ? null : body['address']
            // Strictly Admin or Sale. `isEmpty` only recognises
            // undefined/null/'', so `true`, `1.9` or junk used to reach the
            // Backend untouched -- and the Backend binds what it is given.
            body['privilege'] = persistablePrivilege(body['privilege'])

            let res: any;
            try {
                res = await api_employee(operation, body, key_id);
            } catch (err) {
                // The Backend now refuses a login code that is taken (409
                // EMPLOYEE_CODE_TAKEN) or malformed (400
                // EMPLOYEE_CODE_INVALID), and each answer carries a sentence
                // naming the problem. There was no catch here at all, so a
                // rejected promise showed the operator nothing: no success,
                // no error, the dialog simply stayed open.
                Modal.error({
                    ...getConfigModal('ผิดพลาด', writeFailureMessage(
                        err,
                        operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ')),
                });
                return;
            }
            if (res === 1) {
                setOpen(false);
                Modal.success({
                    ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขข้อมูลสำเร็จแล้ว' : 'เพิ่มข้อมูลสำเร็จแล้ว'),
                    ...{ onOk() { init(); }}
                });
            } else {
                Modal.error({
                    ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ' : 'เพิ่มข้อมูลไม่สำเร็จ'),
                });
            }
        } else {
            Modal.warning({
                ...getConfigModal('แจ้งเตือน', 'กรุณากรอกข้อมูลให้ครบถ้วน')
            });
        }
    }

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', 'คุณต้องการลบข้อมูลนี้หรือไม่ ?'),
            ...{
                async onOk() { 
                    try {
                        let res = await api_employee('DELETE', {}, key_id);
                        if (res === 1) {
                            Modal.success({
                                ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                                ...{ onOk() { init(); }}
                            });
                        } else {
                            Modal.error({
                                ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                            });
                        }
                    } catch (err: any) {
                        // Backend answers a record that is already gone with a
                        // specific 404 and a sentence written for the user. Showing
                        // only "ลบข้อมูลไม่สำเร็จ" threw that away and left them
                        // guessing whether it was the network, their permissions, or
                        // a colleague who deleted it first.
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                        });
                    }
                }
            }
        });
    }

    const onDataDetailChange = (payload: any) => {
        setEmployee(payload)
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <>
            {/* { data.length > 0 ? <CommonTableList page='employee' placeholder={placeholder_txt} columns={columns} data={data} filterField={filterField} onAdd={() => { onShowDetail('ADD') }} /> : null } */}
            <CommonTableList page='employee' placeholder={placeholder_txt} columns={columns} data={[...data]} filterField={filterField} onAdd={() => { onShowDetail('ADD') }} />
            <CommonDetail width='1150px' mode={mode} title={title} open={open} onClosed={onCloseDetail} onSaved={onSave} element={ <EmployeeDetail employee={{...employee}} onChange={onDataDetailChange} /> } />
        </>
    );
}

export default Employee;